'use strict';

import {refreshContentCertificateContext, setContentCompletionState} from '../services/teejit-content-certificate.js';
import {khRememberContentProgressAnalytics} from '../services/teejit-content-progress.js';

const qlpDefaults = {
    yourResult: 'Your result',
    greatJob: 'Great job!',
    notSoGood: 'Not so good this time. Try again!',
    passed: 'Great job, you passed the assessment.',
    notPassed: 'Assessment not passed yet.',
    answerReview: 'Answer Review',
    restart: 'Restart',
    readAloud: 'Read aloud',
    correctAnswered: 'Correctly answered',
    incorrectAnswered: 'Incorrectly answered',
    yourAnswer: 'Your answer',
    correctAnswer: 'Correct answer',
    noResultAvailable: 'No result available.',
    pointsReceived: 'Points received',
    detailsNotAvailable: 'Detailed question feedback is not available for this attempt.',
    repeatTest: 'Repeat the test',
    yourScore: 'Your score',
    submitting: 'Submitting assessment',
    submittingWait: 'Submitting assessment, please wait...',
    starting: 'Starting',
    points: 'Points',
    submitFailed: 'Submit failed',
    alreadySubmitted: 'This assessment already has a submitted result.',
    answerAtLeastOne: 'Please answer at least one question before submitting.',
    confirmationCompleted: 'Confirmation completed.',
    confirmationSaved: 'Your confirmation has been saved.',
};

const getQlp = (root) => {
    const source = root?.dataset?.qlpStarting ? root : (root?.querySelector('#quiz-root') || root);

    return {
        yourResult: source?.dataset.qlpResult || qlpDefaults.yourResult,
        greatJob: source?.dataset.qlpGreat || qlpDefaults.greatJob,
        notSoGood: source?.dataset.qlpNotgood || qlpDefaults.notSoGood,
        passed: source?.dataset.qlpPassed || qlpDefaults.passed,
        notPassed: source?.dataset.qlpNotpassed || qlpDefaults.notPassed,
        answerReview: source?.dataset.qlpReview || qlpDefaults.answerReview,
        restart: source?.dataset.qlpRestart || qlpDefaults.restart,
        readAloud: source?.dataset.qlpReadaloud || qlpDefaults.readAloud,
        correctAnswered: source?.dataset.qlpCorrectanswered || qlpDefaults.correctAnswered,
        incorrectAnswered: source?.dataset.qlpIncorrectanswered || qlpDefaults.incorrectAnswered,
        yourAnswer: source?.dataset.qlpYouranswer || qlpDefaults.yourAnswer,
        correctAnswer: source?.dataset.qlpCorrectanswer || qlpDefaults.correctAnswer,
        noResultAvailable: source?.dataset.qlpNoresultavailable || qlpDefaults.noResultAvailable,
        pointsReceived: source?.dataset.qlpPointsreceived || qlpDefaults.pointsReceived,
        detailsNotAvailable: source?.dataset.qlpDetailsnotavailable || qlpDefaults.detailsNotAvailable,
        repeatTest: source?.dataset.qlpRepeattest || qlpDefaults.repeatTest,
        yourScore: source?.dataset.qlpYourscore || qlpDefaults.yourScore,
        submitting: source?.dataset.qlpSubmitting || qlpDefaults.submitting,
        submittingWait: source?.dataset.qlpSubmittingwait || qlpDefaults.submittingWait,
        starting: source?.dataset.qlpStarting || qlpDefaults.starting,
        points: source?.dataset.qlpPoints || qlpDefaults.points,
        submitFailed: source?.dataset.qlpSubmitfailed || qlpDefaults.submitFailed,
        alreadySubmitted: source?.dataset.qlpAlreadysubmitted || qlpDefaults.alreadySubmitted,
        answerAtLeastOne: source?.dataset.qlpAnsweratleastone || qlpDefaults.answerAtLeastOne,
        confirmationCompleted: source?.dataset.qlpConfirmationcompleted || qlpDefaults.confirmationCompleted,
        confirmationSaved: source?.dataset.qlpConfirmationsaved || qlpDefaults.confirmationSaved,
    };
};

const getKhRestBaseUrl = () => String(window.khThemeRuntime?.restBaseUrl || '/wp-json/knowhub/v2').replace(/\/+$/, '');

const getKhFrontendLocale = () => {
    const locale = window.khThemeRuntime?.frontendLocale || 'de_DE';
    return typeof locale === 'string' ? locale.trim() : '';
};

const invalidateRuntimeCaches = () => {
    if (typeof window.khInvalidateRuntimeDataCaches === 'function') {
        window.khInvalidateRuntimeDataCaches('POST');
    }
};

const khRestAjax = ({path, data = {}, success, error, complete}) => {
    const accessToken = String(window.khThemeRuntime?.userAccessToken || '');
    const payload = {
        ...data,
        user_access_token: accessToken,
    };
    const frontendLocale = getKhFrontendLocale();
    if (frontendLocale && !payload.frontend_locale && !payload.locale) {
        payload.frontend_locale = frontendLocale;
    }

    const headers = {
        ...(accessToken ? {Authorization: 'Bearer ' + accessToken} : {}),
        ...(frontendLocale ? {'X-Frontend-Locale': frontendLocale} : {}),
    };

    jQuery.ajax({
        type: 'POST',
        url: getKhRestBaseUrl() + '/' + String(path || '').replace(/^\/+/, ''),
        data: JSON.stringify(payload),
        contentType: 'application/json',
        dataType: 'json',
        headers,
        success: function (responseData, textStatus, jqXHR) {
            invalidateRuntimeCaches();

            if (typeof success === 'function') {
                success(responseData, textStatus, jqXHR);
            }
        },
        error,
        complete,
    });
};

const activateTestTab = (root, tab) => {
    if (!root || !tab) {
        return;
    }

    const links = root.querySelectorAll('[data-test-tab-target]');
    const panels = root.querySelectorAll('[data-test-tab-content]');

    links.forEach((link) => {
        const isActive = link.getAttribute('data-test-tab-target') === tab;
        const listItem = link.querySelector('.t-content-navi-element');

        link.setAttribute('aria-selected', isActive ? 'true' : 'false');
        link.setAttribute('tabindex', isActive ? '0' : '-1');

        if (listItem) {
            listItem.classList.toggle('active', isActive);
        }
    });

    panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-test-tab-content') === tab;
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
};

const scrollToAccordionTop = (root) => {
    if (!root) {
        return;
    }

    const accordionItem = root.closest('.accordion-item');
    if (!accordionItem) {
        return;
    }

    window.requestAnimationFrame(() => {
        const accordionHeader = accordionItem.querySelector('summary.accordion-header') || accordionItem;

        if (typeof window.khScrollElementBelowTopbar === 'function') {
            window.khScrollElementBelowTopbar(accordionHeader);
            return;
        }

        const headerOffset = typeof window.khGetFixedTopOffset === 'function' ? window.khGetFixedTopOffset() : 120;
        const accordionTop = accordionHeader.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
            top: Math.max(accordionTop - headerOffset, 0),
            behavior: 'smooth',
        });
    });
};

const getTabLinks = (root) => {
    if (!root) {
        return [];
    }
    return Array.from(root.querySelectorAll('[data-test-tab-target]')).filter((link) => !link.hidden);
};

const getQuestionFieldsets = (root) => {
    if (!root) {
        return [];
    }
    return Array.from(root.querySelectorAll('[data-question-id]'));
};

const toInt = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const toBool = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return value !== 0;
    }
    const normalized = String(value ?? '').trim().toLowerCase();
    if (['1', 'true', 'yes', 'y'].includes(normalized)) {
        return true;
    }
    if (['0', 'false', 'no', 'n', ''].includes(normalized)) {
        return false;
    }
    return false;
};

const isPassed = (result, passingScore) => {
    if (Object.prototype.hasOwnProperty.call(result, 'passed')) {
        return toBool(result.passed);
    }

    const maxScore = toInt(result.max_score ?? 0);
    const targetScore = passingScore <= 0
        ? maxScore
        : (passingScore > maxScore ? Math.ceil(maxScore * Math.min(passingScore, 100) / 100) : passingScore);
    return maxScore > 0 && toInt(result.score ?? 0) >= targetScore;
};

const isConfirmationVariant = (root) => {
    return String(root?.getAttribute('data-assessment-variant') || '').trim() === 'confirmation';
};

const setQuestionsTabVisible = (root, visible) => {
    if (!root) {
        return;
    }
    root.querySelectorAll('[data-test-questions-link], [data-test-open-questions]').forEach((control) => {
        control.hidden = !visible;
    });
};

const clearSelectedAnswers = (root) => {
    if (!root) {
        return;
    }

    root.querySelectorAll('[data-question-id] input[data-option-id]').forEach((input) => {
        input.checked = false;
        input.removeAttribute('checked');
    });
};

const getSelectedOptionIds = (questionFieldset) => {
    const checkedOptions = questionFieldset.querySelectorAll('input[data-option-id]:checked');
    const selected = Array.from(checkedOptions)
        .map((input) => toInt(input.getAttribute('data-option-id')))
        .filter((id) => id > 0);

    return Array.from(new Set(selected));
};

const buildAnswersPayload = (root) => {
    const questions = getQuestionFieldsets(root);
    const answers = [];

    questions.forEach((questionFieldset) => {
        const questionId = toInt(questionFieldset.getAttribute('data-question-id'));
        if (questionId <= 0) {
            return;
        }

        const selectedOptionIds = getSelectedOptionIds(questionFieldset);
        if (!selectedOptionIds.length) {
            return;
        }

        answers.push({
            question_id: questionId,
            selected_option_ids: selectedOptionIds,
        });
    });

    return answers;
};

const enforceConfirmationSingleSelect = (input) => {
    const questionFieldset = input.closest('[data-question-id]');
    if (!questionFieldset) {
        return;
    }

    const questionType = String(questionFieldset.getAttribute('data-question-type') || '');
    if (questionType !== 'confirmation') {
        return;
    }

    if (!input.checked) {
        return;
    }

    const checkboxes = questionFieldset.querySelectorAll('input[type="checkbox"][data-option-id]');
    checkboxes.forEach((checkbox) => {
        if (checkbox !== input) {
            checkbox.checked = false;
        }
    });
};

const escapeHtml = (value) => {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const extractAjaxErrorMessage = (xhr, fallback = 'Unknown error') => {
    const response = xhr?.responseJSON || null;
    const candidates = [
        response?.data?.message,
        response?.message,
        response?.error,
        response?.data?.submit_error?.message,
        response?.submit_error?.message,
        response?.data?.start_response?.message,
        response?.start_response?.message,
    ];

    const message = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim() !== '');
    if (message) {
        return message;
    }

    return xhr?.statusText || fallback;
};

/*
const getQuestionMeta = (root) => {
    const map = new Map();
    const serializedMeta = String(root?.getAttribute('data-question-meta') || '').trim();
    if (serializedMeta) {
        try {
            const parsed = JSON.parse(serializedMeta);
            if (parsed && typeof parsed === 'object') {
                Object.keys(parsed).forEach((questionIdKey) => {
                    const questionId = toInt(questionIdKey);
                    if (questionId <= 0) {
                        return;
                    }
                    const meta = parsed[questionIdKey] || {};
                    const optionMap = new Map();
                    const options = meta.options && typeof meta.options === 'object' ? meta.options : {};
                    Object.keys(options).forEach((optionIdKey) => {
                        const optionId = toInt(optionIdKey);
                        if (optionId > 0) {
                            optionMap.set(optionId, String(options[optionIdKey] ?? `Option ${optionId}`));
                        }
                    });

                    map.set(questionId, {
                        title: String(meta.title || `Question ${questionId}`),
                        options: optionMap,
                    });
                });
            }
        } catch (error) {
            console.warn('[TJKH][TEST] Failed to parse question meta:', error);
        }
    }

    const fieldsets = root ? root.querySelectorAll('[data-question-id]') : [];

    fieldsets.forEach((fieldset) => {
        const questionId = toInt(fieldset.getAttribute('data-question-id'));
        if (questionId <= 0) {
            return;
        }

        const legendText = fieldset.querySelector('legend')?.textContent?.trim() || `Question ${questionId}`;
        const optionMap = new Map();
        const optionInputs = fieldset.querySelectorAll('input[data-option-id]');
        optionInputs.forEach((input) => {
            const optionId = toInt(input.getAttribute('data-option-id'));
            if (optionId <= 0) {
                return;
            }

            const labelText = input.closest('label')?.querySelector('span')?.textContent?.trim() || `Option ${optionId}`;
            optionMap.set(optionId, labelText);
        });

        map.set(questionId, {
            title: legendText,
            options: optionMap,
        });
    });

    return map;
};
*/
const getQuestionMeta = (root) => {
    const map = new Map();
    const serializedMeta = String(root?.getAttribute('data-question-meta') || '').trim();
    if (serializedMeta) {
        try {
            const parsed = JSON.parse(serializedMeta);
            if (parsed && typeof parsed === 'object') {
                Object.keys(parsed).forEach((questionIdKey) => {
                    const questionId = toInt(questionIdKey);
                    if (questionId <= 0) {
                        return;
                    }
                    const meta = parsed[questionIdKey] || {};
                    const optionMap = new Map();
                    const options = meta.options && typeof meta.options === 'object' ? meta.options : {};
                    Object.keys(options).forEach((optionIdKey) => {
                        const optionId = toInt(optionIdKey);
                        if (optionId > 0) {
                            optionMap.set(optionId, String(options[optionIdKey] ?? `Option ${optionId}`));
                        }
                    });

                    map.set(questionId, {
                        title: String(meta.title || `Question ${questionId}`),
                        options: optionMap,
                    });
                });
            }
        } catch (error) {
            console.warn('[TJKH][TEST] Failed to parse question meta:', error);
        }
    }

    // ── NUR als Fallback, wenn JSON-Meta keine Daten hatte ──
    const fieldsets = root ? root.querySelectorAll('[data-question-id]') : [];

    fieldsets.forEach((fieldset) => {
        const questionId = toInt(fieldset.getAttribute('data-question-id'));
        if (questionId <= 0) {
            return;
        }

        // Bereits aus JSON vorhanden → nicht überschreiben
        if (map.has(questionId)) {
            return;
        }

        const legendEl = fieldset.querySelector('legend');
        const divEl = fieldset.querySelector('[style*="font-weight"]');
        const legendText = legendEl?.textContent?.trim()
            || divEl?.textContent?.trim()
            || `Question ${questionId}`;

        const optionMap = new Map();
        const optionInputs = fieldset.querySelectorAll('input[data-option-id]');
        optionInputs.forEach((input) => {
            const optionId = toInt(input.getAttribute('data-option-id'));
            if (optionId <= 0) {
                return;
            }

            const labelText = input.closest('label')?.querySelector('span')?.textContent?.trim() || `Option ${optionId}`;
            optionMap.set(optionId, labelText);
        });

        map.set(questionId, {
            title: legendText,
            options: optionMap,
        });
    });

    return map;
};

const initResultFromDataset = (root) => {
    if (!root) {
        return;
    }

    const serializedResult = String(root.getAttribute('data-initial-result') || '').trim();
    if (!serializedResult) {
        return;
    }

    let result = null;
    try {
        result = JSON.parse(serializedResult);
    } catch (error) {
        console.warn('[TJKH][TEST] Failed to parse initial result:', error);
        return;
    }

    if (!result || typeof result !== 'object') {
        return;
    }

    const resultTarget = root.querySelector('[data-test-result-output]');
    if (!resultTarget) {
        return;
    }

    const passingScore = toInt(root.getAttribute('data-passing-score'));
    const canRestart = String(root.getAttribute('data-can-restart') || '') === '1';
    if (isConfirmationVariant(root)) {
        result.assessmentVariant = 'confirmation';
    }
    resultTarget.innerHTML = renderResultHtml(result, getQuestionMeta(root), passingScore, canRestart, getQlp(root));
};

const idsToText = (ids, optionMap) => {
    if (!Array.isArray(ids) || !ids.length) {
        return 'None';
    }
    return ids.map((id) => optionMap.get(toInt(id)) || `#${id}`).join(', ');
};

const renderResultHtml = (result, questionMeta, passingScore = 0, canRestart = false, qlp = qlpDefaults) => {
    if (!result || typeof result !== 'object') {
        return `<p>${qlp.noResultAvailable}</p>`;
    }

    if (result.assessmentVariant === 'confirmation') {
        return `
            <div class="quiz-result-banner quiz-result-banner-passed t-confirmation-result">
                <div class="quiz-result-banner-left">
                    <span class="quiz-result-banner-label">${escapeHtml(qlp.confirmationCompleted)}</span>
                    <span class="quiz-result-banner-msg">${escapeHtml(qlp.confirmationSaved)}</span>
                </div>
            </div>
            ${canRestart ? `<button type="button" class="btn btn-primary" data-test-restart>${escapeHtml(qlp.repeatTest)}</button>` : ''}
        `;
    }

    const score = toInt(result.score ?? 0);
    const maxScore = toInt(result.max_score ?? 0);
    const status = result.status ?? '';
    const attemptId = result.attempt_id ?? '-';
    const percent = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    const passed = isPassed(result, passingScore);
    const answers = Array.isArray(result.answers) ? result.answers : [];

    const detailsHtml = answers.length
        ? answers.map((answer, index) => {
            const qId = toInt(answer.question_id);
            const meta = questionMeta.get(qId) || { title: `Question ${qId || index + 1}`, options: new Map() };
            const correct = !!answer.is_correct;
            const earnedPoints = toInt(answer.earned_points ?? 0);
            const selected = idsToText(answer.selected_option_ids || [], meta.options);
            const correctOptions = idsToText(answer.correct_option_ids || [], meta.options);

            return `
                    <fieldset class="${correct ? 'question-correct' : 'question-wrong'}">
                        <div>${escapeHtml(meta.title)}</div>
                        <div class="quiz-result-question-tags">
                            <span class="t-tag ${correct ? 't-tag-category' : 't-tag-audience'}" style="${correct ? 'border-color:var(--teejit-success);color:var(--teejit-success);' : 'border-color:var(--teejit-error);color:var(--teejit-error);'}">${correct ? qlp.correctAnswered : qlp.incorrectAnswered}</span>
                            <span class="t-tag t-tag-audience">${qlp.pointsReceived}: ${earnedPoints}</span>
                        </div>
                        <div class="quiz-question-labels">
                            <label class="submitted ${correct ? 'answer-correct' : 'answer-wrong'}">
                                <div class="t-test-option-wrap">
                                    <input type="radio" ${selected ? 'checked' : ''}>
                                </div>
                                <span>${correct ? qlp.correctAnswer : qlp.yourAnswer}: ${escapeHtml(selected)}</span>
                            </label>
                            ${!correct ? `
                            <label class="submitted answer-correct">
                                <div class="t-test-option-wrap">
                                    <input type="radio">
                                </div>
                                <span>${qlp.correctAnswer}: ${escapeHtml(correctOptions)}</span>
                            </label>
                            ` : ''}
                        </div>
                    </fieldset>
            `;
        }).join('')
        : `<p>${qlp.detailsNotAvailable}</p>`;

    const restartHtml = canRestart ? `
        <div class="quiz-repeat-button">
            <button type="button" class="btn btn-primary" data-test-restart>${qlp.repeatTest}</button>
        </div>
    ` : '';

    return `
        <div class="quiz-result-banner ${passed ? 'quiz-result-banner-passed' : 'quiz-result-banner-not-passed'}" id="quizResultBanner" >
            <div class="quiz-result-banner-left">
                <span class="quiz-result-banner-label ">${qlp.yourResult}:</span>
                <span class="quiz-result-banner-msg" id="quizResultMsg">${passed ? qlp.passed : qlp.notPassed}</span>
            </div>
            <!--<div class="quiz-result-banner-score score-bad" id="quizResultScore">${score}<span class="score-slash">/${maxScore}</span></div>-->
        </div>
        
        <div class="t-test-result-tags"">
            <span class="t-tag t-tag-test test-result-score">${qlp.yourScore}: ${score} / ${maxScore} ${qlp.points}</span>
            <span class="t-tag t-tag-test test-result-percent">${qlp.yourResult}: ${percent}%</span>
        </div>
        
        <div class="quiz-answer-review-container">
            <div class="quiz-result-headline"><h4>${qlp.answerReview}</h4></div>
            <div class="quiz-result-answer-review">${detailsHtml}</div>
        </div>
        
        ${restartHtml}
    `;
};

jQuery(document).on('click', '[data-test-open-questions]', function (event) {
    event.preventDefault();

    const root = this.closest('[data-test-tabs-root]');
    if (!root || root.getAttribute('data-assessment-locked') === '1') {
        return;
    }

    activateTestTab(root, 'questions');
    root.querySelector('[data-test-questions-link]')?.focus();
    scrollToAccordionTop(root);
});

jQuery(document).on('click', '[data-test-tab-target]', function (event) {
    event.preventDefault();

    const root = this.closest('[data-test-tabs-root]');
    const tab = this.getAttribute('data-test-tab-target');

    activateTestTab(root, tab);

    if (tab === 'show-result') {
        scrollToAccordionTop(root);
    }
});

jQuery(document).on('keydown', '[data-test-tab-target]', function (event) {
    const root = this.closest('[data-test-tabs-root]');
    const links = getTabLinks(root);

    if (!links.length) {
        return;
    }

    const currentIndex = links.indexOf(this);
    if (currentIndex < 0) {
        return;
    }

    let nextIndex = currentIndex;

    switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
            nextIndex = (currentIndex + 1) % links.length;
            break;
        case 'ArrowLeft':
        case 'ArrowUp':
            nextIndex = (currentIndex - 1 + links.length) % links.length;
            break;
        case 'Home':
            nextIndex = 0;
            break;
        case 'End':
            nextIndex = links.length - 1;
            break;
        default:
            return;
    }

    event.preventDefault();

    const nextLink = links[nextIndex];
    if (!nextLink) {
        return;
    }

    nextLink.focus();
    const nextTab = nextLink.getAttribute('data-test-tab-target');
    activateTestTab(root, nextTab);

    if (nextTab === 'show-result') {
        scrollToAccordionTop(root);
    }
});

jQuery(document).on('change', '[data-question-id] input[data-option-id]', function () {
    enforceConfirmationSingleSelect(this);
});

jQuery(() => {
    document.querySelectorAll('[data-test-tabs-root]').forEach((root) => {
        initResultFromDataset(root);
    });
});

jQuery(document).on('click', '[data-test-restart]', function (event) {
    event.preventDefault();

    const button = this;
    const root = button.closest('[data-test-tabs-root]');
    if (!root) {
        return;
    }

    const assessmentId = toInt(root.getAttribute('data-assessment-id'));
    const contentId = toInt(root.getAttribute('data-content-id'));
    if (assessmentId <= 0) {
        console.error('[TJKH][TEST] Missing assessment_id on root.');
        return;
    }

    const resultTarget = root.querySelector('[data-test-result-output]');
    const qlp = getQlp(root);
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = qlp.starting + '...';

    khRestAjax({
        path: 'assessment/' + encodeURIComponent(assessmentId) + '/restart',
        data: {
            assessment_id: assessmentId,
            content_id: contentId,
        },
        success: () => {
            root.setAttribute('data-assessment-locked', '0');
            root.setAttribute('data-can-restart', '0');
            clearSelectedAnswers(root);
            setQuestionsTabVisible(root, true);
            activateTestTab(root, 'questions');
        },
        error: (xhr) => {
            const message = extractAjaxErrorMessage(xhr, 'Unknown restart error');
            if (resultTarget) {
                resultTarget.innerHTML = `<p>Neustart fehlgeschlagen: ${escapeHtml(message)}</p>`;
            }
            activateTestTab(root, 'show-result');
        },
        complete: () => {
            button.disabled = false;
            button.textContent = originalText;
        },
    });
});

jQuery(document).on('click', '[data-test-submit]', function (event) {
    event.preventDefault();

    const button = this;
    const root = button.closest('[data-test-tabs-root]');
    if (!root) {
        return;
    }

    const assessmentId = toInt(root.getAttribute('data-assessment-id'));
    const contentId = toInt(root.getAttribute('data-content-id'));
    if (assessmentId <= 0) {
        console.error('[TJKH][TEST] Missing assessment_id on root.');
        return;
    }

    const assessmentLocked = String(root.getAttribute('data-assessment-locked') || '') === '1';
    const confirmationVariant = isConfirmationVariant(root);
    const answers = buildAnswersPayload(root);
    const resultTarget = root.querySelector('[data-test-result-output]');
    const confirmationMessage = root.querySelector('[data-test-confirmation-message]');
    const qlp = getQlp(root);

    if (confirmationMessage) {
        confirmationMessage.textContent = '';
    }

    if (assessmentLocked) {
        if (resultTarget) {
            resultTarget.innerHTML = '<p>' + escapeHtml(qlp.alreadySubmitted) + '</p>';
        }
        activateTestTab(root, 'show-result');
        scrollToAccordionTop(root);
        return;
    }

    if (!answers.length) {
        if (confirmationVariant && confirmationMessage) {
            confirmationMessage.textContent = qlp.answerAtLeastOne;
            return;
        }

        if (resultTarget) {
            resultTarget.innerHTML = '<p>' + escapeHtml(qlp.answerAtLeastOne) + '</p>';
        }
        activateTestTab(root, 'show-result');
        scrollToAccordionTop(root);
        return;
    }

    const payload = {
        assessment_id: assessmentId,
        content_id: contentId,
        answers: answers,
    };

    if (window.khThemeRuntime?.debugAjax) {
        console.groupCollapsed('[TJKH][TEST][SUBMIT] sending assessment answers');
        console.log('assessment_id:', assessmentId);
        console.log('answers:', answers);
        console.groupEnd();
    }

    if (resultTarget) {
        resultTarget.innerHTML = `<p>${escapeHtml(qlp.submittingWait)}</p>`;
    }

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = qlp.submitting + '...';

    khRestAjax({
        path: 'assessment/' + encodeURIComponent(assessmentId) + '/submit',
        data: payload,
        success: (response) => {
            const result = response?.data?.result || response?.data?.submit_response || null;
            // Only persist progress confirmed by server-side grading.
            khRememberContentProgressAnalytics(response?.data?.content_analytics);

            if (window.khThemeRuntime?.debugAjax) {
                console.groupCollapsed('[TJKH][TEST][SUBMIT] success');
                console.log('rawResponse:', response);
                console.log('result:', result);
                console.groupEnd();
            }

            if (resultTarget) {
                const passingScore = toInt(root.getAttribute('data-passing-score'));
                if (confirmationVariant && result && typeof result === 'object') {
                    result.assessmentVariant = 'confirmation';
                }
                const passed = result && typeof result === 'object' && isPassed(result, passingScore);
                const canRestart = !confirmationVariant;

                root.setAttribute('data-assessment-locked', '1');
                root.setAttribute('data-can-restart', canRestart ? '1' : '0');
                setQuestionsTabVisible(root, false);
                setContentCompletionState(root.closest('.accordion-item'), passed);
                refreshContentCertificateContext(root.closest('.accordion-item'));

                resultTarget.innerHTML = renderResultHtml(result, getQuestionMeta(root), passingScore, canRestart, qlp);
            }

            activateTestTab(root, 'show-result');
            scrollToAccordionTop(root);
        },
        error: (xhr) => {
            const message = extractAjaxErrorMessage(xhr, 'Unknown submit error');
            if (window.khThemeRuntime?.debugAjax) {
                console.group('[TJKH][TEST][SUBMIT] error');
                console.log('status:', xhr?.status);
                console.log('responseJSON:', xhr?.responseJSON);
                console.log('responseText:', xhr?.responseText);
                console.groupEnd();
            }
            if (resultTarget) {
                resultTarget.innerHTML = `<p>${qlp.submitFailed}: ${escapeHtml(message)}</p>`;
            }
            activateTestTab(root, 'show-result');
            scrollToAccordionTop(root);
        },
        complete: () => {
            button.disabled = false;
            button.textContent = originalText;
        },
    });
});
